// The review screen's seekable transport (`client-rounds-review`; ported from
// `prototype/sketches/rounds-slice/review-screen.html`).
//
// This is deliberately NOT `ReplayControls`. Live-mode replay works by re-simulation, so
// reaching tick *N* costs O(N) and arbitrary seek is deferred (**D-123**) — its position
// strip is `role="presentation"`, read-only, and has no thumb. A batch trace is a
// **seekable file** (**A4**): a `tick → byte offset` index makes any tick an O(1) range
// read, so the draggable thumb below is this screen's headline affordance and the reason
// the two transports are two components rather than one that is draggable-or-not
// depending on invisible provenance (**D5**).
//
// The other deliberate difference: the speed steps start at **½×**. Scrubbing an
// already-simulated batch trace for diagnosis wants slow-motion far more than
// fast-forward, which is the opposite of the live transport's 1×-and-up ladder.

import { useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import {
  REVIEW_SPEED_STEPS,
  clockLabel,
  fractionAtTick,
  speedLabel,
  tickAtFraction,
  transportClock,
  type TierStateDescriptor,
} from '../../rounds/review-model';
import type { EvictedSpan } from '../../../net/rounds-trace';
import { TierIndicator } from './TierIndicator';

// The DESIGN §14 season split (40% / 35% / 25%) tinted as decorative bands behind the
// track — advisory context for *where* you are scrubbing, exactly as the live strip uses
// them. Duplicated rather than imported from `ReplayControls`, which keeps its copy
// private (the `Summary.tsx` "duplicated, not imported" precedent).
const SEASON_BANDS: ReadonlyArray<{ frac: number; colour: string; name: string }> = [
  { frac: 0.4, colour: 'var(--season-summer)', name: 'Summer' },
  { frac: 0.35, colour: 'var(--season-autumn)', name: 'Autumn' },
  { frac: 0.25, colour: 'var(--season-winter)', name: 'Winter' },
];

/**
 * The seek thumb's width, and half of it (UI/UX audit **F-20**).
 *
 * Named constants rather than literals because **three** things must agree on them: the thumb's own
 * box, the rail its position is expressed against, and `tickAtClientX`'s divisor. The defect these
 * replace was exactly a disagreement — the thumb was centred on a full-width rail, so at position 0
 * it hung 7px outside the track it belonged to, and any fix to the rendering alone would have made
 * dragging and the thumb disagree instead.
 */
const SEEK_THUMB_WIDTH_PX = 16;
const SEEK_RAIL_INSET_PX = SEEK_THUMB_WIDTH_PX / 2;

/** How far one arrow-key press seeks — keyboard parity for the drag affordance. */
const KEY_STEP_TICKS = 1;
/** How far Page Up/Down seeks. */
const PAGE_STEP_FRACTION = 0.05;

const BTN_BASE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  border: '1.5px solid var(--hud-line)',
  borderRadius: 999,
  cursor: 'pointer',
  background: 'var(--surface-card)',
  color: 'var(--hud-text)',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 14px',
  fontSize: 13,
};

export interface SeekTransportProps {
  firstTick: number;
  terminalTick: number;
  tickRateHz: number;
  /** The current playback position, in ticks (may be fractional between frames). */
  position: number;
  playing: boolean;
  speed: number;
  /** The current tier, rendered by the indicator inside the bar. */
  tier: TierStateDescriptor;
  /**
   * Evicted tick ranges to mark on the track, hatched, **before** the player scrubs into
   * them (`rounds-trace-retention`, **D6**'s foresight half). Beside the existing
   * `SEASON_BANDS` decorative-band mechanism, but positioned at the specific ranges
   * reported rather than proportionally filling the whole track. Defaults to none —
   * every trace before eviction, and every trace whose detail is fully retained.
   */
  evictedSpans?: readonly EvictedSpan[];
  /** Drag/keyboard seek: reposition playback to an arbitrary tick. */
  onSeek(tick: number): void;
  /** Called when a drag begins/ends, so the caller can hold playback while scrubbing. */
  onDragChange?(dragging: boolean): void;
  onTogglePlay(): void;
  onSpeed(step: number): void;
}

export function SeekTransport({
  firstTick,
  terminalTick,
  tickRateHz,
  position,
  playing,
  speed,
  tier,
  evictedSpans = [],
  onSeek,
  onDragChange,
  onTogglePlay,
  onSpeed,
}: SeekTransportProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const tick = Math.floor(position);
  const fraction = fractionAtTick(position, firstTick, terminalTick);
  /**
   * **One rail, used by both the rendering and the pointer mapping** (UI/UX audit **F-20**).
   *
   * The thumb is 16px wide and centred on its position, so a rail spanning the track's full width
   * puts half the thumb outside the track at each extreme — measured 7px past the left edge at
   * position 0, which is the state the player sees first. The rail is therefore inset by half the
   * thumb at each end, and the thumb's travel is `width - 16px` rather than `width`.
   *
   * The critical part is that `tickAtClientX` divides by **the same** inset span. A visual-only clamp
   * would desync dragging from the thumb near both ends: the pointer would name one tick while the
   * thumb sat at another. Rendering geometry and hit geometry are the same expression here, so they
   * cannot disagree.
   */
  const fillPct = `calc(${SEEK_RAIL_INSET_PX}px + ${fraction} * (100% - ${SEEK_THUMB_WIDTH_PX}px))`;

  /** The tick a client-x names on the track. An unmeasured container (jsdom with no
   * layout stub) yields no seek rather than a divide-by-zero — the `Match.tsx`
   * unmeasured-container precedent. */
  function tickAtClientX(clientX: number): number | null {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) {
      return null;
    }
    // The same inset rail the thumb is drawn on — see `fillPct`. A track narrower than the thumb has
    // no rail to speak of, so it falls back to the full width rather than dividing by <= 0.
    const rail = rect.width - SEEK_THUMB_WIDTH_PX;
    if (rail <= 0) {
      return tickAtFraction((clientX - rect.left) / rect.width, firstTick, terminalTick);
    }
    return tickAtFraction((clientX - rect.left - SEEK_RAIL_INSET_PX) / rail, firstTick, terminalTick);
  }

  function seekTo(clientX: number): void {
    const target = tickAtClientX(clientX);
    if (target !== null) {
      onSeek(target);
    }
  }

  function beginDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    draggingRef.current = true;
    onDragChange?.(true);
    // jsdom implements neither method; production browsers keep the pointer on the
    // track so a drag survives leaving its 14px height.
    (event.currentTarget as Element & { setPointerCapture?(id: number): void }).setPointerCapture?.(
      event.pointerId,
    );
    seekTo(event.clientX);
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    if (!draggingRef.current) {
      return;
    }
    seekTo(event.clientX);
    draggingRef.current = false;
    onDragChange?.(false);
    (
      event.currentTarget as Element & { releasePointerCapture?(id: number): void }
    ).releasePointerCapture?.(event.pointerId);
  }

  return (
    <div
      data-testid="review-transport"
      style={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'center',
        width: 'min(600px, calc(100% - 24px))',
      }}
    >
      {/* The scrub track. `role="slider"` is not decoration: it is the accessible form
          of the very affordance the live strip is specified not to have, and it gives
          keyboard parity with the drag. */}
      <div
        ref={trackRef}
        data-testid="review-seek-track"
        role="slider"
        tabIndex={0}
        aria-label="Playback position — drag to seek to any tick"
        aria-valuemin={firstTick}
        aria-valuemax={terminalTick}
        aria-valuenow={tick}
        aria-valuetext={clockLabel(position, tickRateHz)}
        title="Drag to scrub — the trace is a seekable file, so any tick is one range read away"
        onPointerDown={beginDrag}
        onPointerMove={(event) => {
          if (draggingRef.current) {
            seekTo(event.clientX);
          }
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={(event) => {
          const span = terminalTick - firstTick;
          const page = Math.max(1, Math.round(span * PAGE_STEP_FRACTION));
          const deltas: Record<string, number> = {
            ArrowLeft: -KEY_STEP_TICKS,
            ArrowRight: KEY_STEP_TICKS,
            ArrowDown: -KEY_STEP_TICKS,
            ArrowUp: KEY_STEP_TICKS,
            PageDown: -page,
            PageUp: page,
          };
          if (event.key === 'Home') {
            event.preventDefault();
            onSeek(firstTick);
            return;
          }
          if (event.key === 'End') {
            event.preventDefault();
            onSeek(terminalTick);
            return;
          }
          const delta = deltas[event.key];
          if (delta !== undefined) {
            event.preventDefault();
            onSeek(Math.min(terminalTick, Math.max(firstTick, tick + delta)));
          }
        }}
        style={{
          position: 'relative',
          width: '100%',
          height: 14,
          borderRadius: 7,
          background: 'var(--hud-track)',
          border: '1.5px solid var(--hud-line)',
          cursor: 'ew-resize',
          touchAction: 'none',
        }}
      >
        {/* **UI/UX audit F-19.** These bands used to fill the track's whole height, which made them
            indistinguishable from a progress/buffered indicator — the elapsed fill below is also "a
            coloured portion of the track", so a run parked at tick 0 looked partly played. (The
            audit misread it that way itself, which is the finding.) The band is now a thin strip
            along the bottom edge, leaving the full height to mean elapsed and nothing else, and each
            segment carries its season's name so the strip can explain itself on hover rather than
            needing a legend. */}
        <div
          aria-hidden
          data-testid="review-season-strip"
          style={{
            position: 'absolute',
            left: 1,
            right: 1,
            bottom: 1,
            height: 4,
            display: 'flex',
            opacity: 0.85,
            borderRadius: '0 0 6px 6px',
            overflow: 'hidden',
          }}
        >
          {SEASON_BANDS.map((band) => (
            <span
              key={band.colour}
              title={band.name}
              style={{ display: 'block', height: '100%', width: `${band.frac * 100}%`, background: band.colour }}
            />
          ))}
        </div>
        {/* Evicted spans: legible BEFORE the playhead reaches them (**D6**'s foresight
            half), hatched in neutral chrome — never `--danger`, since the bytes were
            deliberately reclaimed, not damaged, and the range stays fully seekable.
            `pointerEvents: 'none'` on every mark is what keeps that true: the overlay
            never intercepts the drag it sits over. */}
        {evictedSpans.length > 0 && (
          <div
            aria-hidden
            style={{ position: 'absolute', inset: 1, borderRadius: 6, overflow: 'hidden', pointerEvents: 'none' }}
          >
            {evictedSpans.map((span) => {
              const left = fractionAtTick(span.fromTick, firstTick, terminalTick) * 100;
              const right = fractionAtTick(span.toTick, firstTick, terminalTick) * 100;
              return (
                <span
                  key={`${span.fromTick}-${span.toTick}`}
                  data-testid="review-evicted-span"
                  data-from-tick={span.fromTick}
                  data-to-tick={span.toTick}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${left}%`,
                    width: `${Math.max(0, right - left)}%`,
                    backgroundImage:
                      'repeating-linear-gradient(45deg, var(--hud-text-dim) 0, var(--hud-text-dim) 2px, transparent 2px, transparent 6px)',
                    opacity: 0.6,
                    pointerEvents: 'none',
                  }}
                />
              );
            })}
          </div>
        )}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 1,
            top: 1,
            bottom: 1,
            width: fillPct,
            background: 'var(--accent)',
            opacity: 0.5,
            borderRadius: '6px 0 0 6px',
          }}
        />
        {/* The thumb IS the affordance: grab-lines, a wider hit target than the visual,
            and a grab cursor. */}
        <div
          data-testid="review-seek-thumb"
          aria-hidden
          style={{
            position: 'absolute',
            left: fillPct,
            top: -5,
            width: SEEK_THUMB_WIDTH_PX,
            height: 24,
            // No centring margin: `fillPct` already places the thumb's own left edge on the rail,
            // so position 0 sits flush with the track start and position 1 flush with its end.
            marginLeft: 0,
            background: 'var(--surface-card)',
            border: '2px solid var(--ink-1)',
            borderRadius: 5,
            boxShadow: 'var(--shadow-hud)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            cursor: 'grab',
          }}
        >
          <i style={{ display: 'block', width: 1.5, height: 10, background: 'var(--ink-3)', borderRadius: 1 }} />
          <i style={{ display: 'block', width: 1.5, height: 10, background: 'var(--ink-3)', borderRadius: 1 }} />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 14px',
          background: 'var(--hud-bg)',
          border: '1.5px solid var(--hud-line)',
          borderRadius: 999,
          boxShadow: 'var(--shadow-hud)',
          maxWidth: '100%',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          type="button"
          data-testid="review-playpause"
          onClick={onTogglePlay}
          style={{
            ...BTN_BASE,
            background: 'var(--accent-fill)',
            borderColor: 'var(--accent-strong)',
            color: '#fff',
            minWidth: 104,
            justifyContent: 'center',
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>{playing ? '⏸' : '▶'}</span>
          {playing ? 'Pause' : 'Play'}
        </button>

        <span
          data-testid="review-clock"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 13,
            color: 'var(--hud-text-dim)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 104,
            textAlign: 'center',
          }}
        >
          {transportClock(position, terminalTick, tickRateHz)}
        </span>

        <span data-testid="review-speeds" style={{ display: 'inline-flex', gap: 4 }}>
          {REVIEW_SPEED_STEPS.map((step) => {
            const active = step === speed;
            return (
              <button
                key={step}
                type="button"
                data-testid={`review-speed-${step}`}
                aria-pressed={active}
                title={`${speedLabel(step)} speed`}
                onClick={() => onSpeed(step)}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 12,
                  minHeight: 44,
                  padding: '0 11px',
                  borderRadius: 999,
                  border: `1.5px solid ${active ? 'var(--accent)' : 'var(--hud-line)'}`,
                  background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
                  color: active ? 'var(--accent-on-soft)' : 'var(--hud-text-dim)',
                  cursor: 'pointer',
                }}
              >
                {speedLabel(step)}
              </button>
            );
          })}
        </span>

        <TierIndicator state={tier} />
      </div>
    </div>
  );
}
